import { cn } from "@/lib/utils";
import { LineItem } from "@/models/line-item";
import { ChevronRightIcon } from "lucide-react";
import React from "react";
import { Button } from "../base/button";

interface PriceSummaryProps {
  lineItems: LineItem[];
  taxRate?: number;
  additionalMarkup?: number;
  isSection?: boolean;
  isCollapsed?: boolean;
  invoiceLateFee?: number;
  isInvoice?: boolean;
  onTotalChange?: (total: number) => void;
}

interface CalculatedPrices {
  price: number;
  markupAmount: number;
  additionalMarkupAmount: number;
  subtotal: number;
  taxableAmount: number;
  taxAmount: number;
  finalPrice: number;
}

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

function calculateLineItemPrice(item: LineItem) {
  const quantity = Number(item.quantity) || 0;
  const unitCost = Number(item.unitCost) || 0;
  return quantity * unitCost;
}

function calculateLineItemTotal(item: LineItem) {
  const quantity = Number(item.quantity) || 0;
  const unitCost = Number(item.unitCost) || 0;
  const markup = Number(item.markup) || 0;
  return quantity * (unitCost * (1 + markup / 100));
}

function calculateLineItemMarkup(item: LineItem) {
  const total = calculateLineItemTotal(item);
  const price = calculateLineItemPrice(item);
  return total - price;
}

export function PriceSummary({
  lineItems,
  taxRate = 0,
  additionalMarkup = 0,
  isSection = false,
  isCollapsed = false,
  invoiceLateFee = undefined,
  isInvoice = false,
  onTotalChange,
}: Readonly<PriceSummaryProps>) {
  const [isBreakdownOpen, setIsBreakdownOpen] = React.useState(false);
  const breakdownRef = React.useRef<HTMLDivElement>(null);
  const [breakdownHeight, setBreakdownHeight] = React.useState<
    number | undefined
  >(undefined);
  const [calculatedPrices, setCalculatedPrices] =
    React.useState<CalculatedPrices>({
      price: 0,
      markupAmount: 0,
      additionalMarkupAmount: 0,
      subtotal: 0,
      taxableAmount: 0,
      taxAmount: 0,
      finalPrice: 0,
    });

  React.useEffect(() => {
    if (breakdownRef.current) {
      setBreakdownHeight(
        isBreakdownOpen ? breakdownRef.current.scrollHeight : 0
      );
    }
  }, [isBreakdownOpen, calculatedPrices]);

  React.useEffect(() => {
    const price = lineItems
      .filter(
        (item) =>
          (item.type !== "Summary" || item.accepted) &&
          item.type !== "GroupStart" &&
          item.type !== "GroupEnd" &&
          !item.parentTextTrackingId
      )
      .reduce((total, item) => {
        return total + calculateLineItemPrice(item);
      }, 0);

    const subtotal = lineItems
      .filter(
        (item) =>
          (item.type !== "Summary" || item.accepted) &&
          item.type !== "GroupStart" &&
          item.type !== "GroupEnd" &&
          !item.parentTextTrackingId
      )
      .reduce((total, item) => {
        return total + calculateLineItemTotal(item);
      }, 0);

    const markupAmount = lineItems
      .filter(
        (item) =>
          (item.type !== "Summary" || item.accepted) &&
          item.type !== "GroupStart" &&
          item.type !== "GroupEnd" &&
          !item.parentTextTrackingId
      )
      .reduce((total, item) => {
        return total + calculateLineItemMarkup(item);
      }, 0);

    const markupableSubtotal = lineItems
      .filter(
        (item) =>
          (item.type !== "Summary" || item.accepted) &&
          item.type !== "GroupStart" &&
          item.type !== "GroupEnd" &&
          !item.excludeAdditionalMarkup &&
          !item.parentTextTrackingId
      )
      .reduce((total, item) => {
        return total + calculateLineItemTotal(item);
      }, 0);

    const taxableSubtotal = lineItems
      .filter(
        (item) =>
          (item.type !== "Summary" || item.accepted) &&
          item.type !== "GroupStart" &&
          item.type !== "GroupEnd" &&
          item.applyTax
      )
      .reduce((total, item) => {
        return total + calculateLineItemTotal(item);
      }, 0);

    const additionalMarkupAmount =
      markupableSubtotal * (additionalMarkup / 100);
    const taxMarkupAmount = taxableSubtotal * (additionalMarkup / 100);

    const taxableAmount = taxableSubtotal + taxMarkupAmount;
    const taxAmount = taxableAmount * (taxRate / 100);

    const finalPrice = subtotal + additionalMarkupAmount + taxAmount;

    onTotalChange?.(finalPrice);

    setCalculatedPrices({
      price,
      markupAmount,
      additionalMarkupAmount,
      subtotal,
      taxableAmount,
      taxAmount,
      finalPrice,
    });
  }, [lineItems, taxRate, additionalMarkup, onTotalChange]);

  if (isCollapsed) {
    return <div>{currencyFormatter.format(calculatedPrices.finalPrice)}</div>;
  } else {
    return (
      <div className="space-y-2">
        <div
          ref={breakdownRef}
          className="overflow-hidden transition-all duration-300 ease-in-out"
          style={{ height: breakdownHeight }}
        >
          <div className="py-2 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Cost</span>
              <span className="text-sm font-medium">
                {currencyFormatter.format(calculatedPrices.price)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Markup</span>
              <span className="text-sm font-medium">
                {currencyFormatter.format(calculatedPrices.markupAmount)}
              </span>
            </div>
            <div className="flex items-center justify-between pt-2 pb-4 border-t">
              <span className="text-base font-medium">Subtotal</span>
              <span className="text-base font-medium">
                {currencyFormatter.format(calculatedPrices.subtotal)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">
                Additional Markup ({additionalMarkup}%)
              </span>
              <span className="text-sm font-medium">
                {currencyFormatter.format(
                  calculatedPrices.additionalMarkupAmount
                )}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">
                Tax ({taxRate}% of{" "}
                {currencyFormatter.format(calculatedPrices.taxableAmount)})
              </span>
              <span className="text-sm font-medium">
                {currencyFormatter.format(calculatedPrices.taxAmount)}
              </span>
            </div>
            { isInvoice && invoiceLateFee !== undefined && (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Late Fee</span>
                <span className="text-sm font-medium">
                  {currencyFormatter.format(invoiceLateFee)}
                </span>
              </div>
            )}
          </div>
        </div>

        <Button
          type="button"
          variant="ghost"
          className="flex items-center justify-start w-full h-6 px-2 mt-1 text-sm text-muted-foreground hover:text-foreground"
          onClick={() => setIsBreakdownOpen(!isBreakdownOpen)}
        >
          <div className="flex items-center justify-start gap-2">
            <ChevronRightIcon
              className={cn(
                "w-4 h-4 transition-transform duration-200",
                isBreakdownOpen ? "-rotate-90" : ""
              )}
            />
            <span>Cost breakdown</span>
          </div>
        </Button>

        <div className="flex items-center justify-between pt-2 border-t">
          <span className="text-base font-medium">
            {isSection ? "Section " : ""}Total
          </span>
          <span className="text-base font-medium">
            {currencyFormatter.format(calculatedPrices.finalPrice)}
          </span>
        </div>
      </div>
    );
  }
}
